// src/services/db.ts
import Dexie, { Table, Transaction } from "dexie";
import { StorageData, Account } from "@/types/metrics";
import { OnboardingStorageData } from "@/types/onboarding";
import { BFStorageData } from "@/types/black-friday";
import {
  MAX_FREE_ACCOUNTS,
  SAMPLE_ACCOUNT_ID,
  SAMPLE_ACCOUNT,
  SHEET_TABS,
} from "@/lib/constants";
// Sample data utils imported dynamically on-demand
import { debounce } from "lodash";

const DEBUG = process.env.NODE_ENV === "development";

function log(action: string, details?: any) {
  if (action.startsWith("Init") || DEBUG) {
    console.log(
      `%c[Dexie ${action}]`,
      "color: #8b5cf6",
      typeof details === "string"
        ? details
        : {
            ...details,
            timestamp: new Date().toISOString(),
          },
    );
  }
}

export interface DataStore extends StorageData {
  id?: number;
  lastUpdated: string;
}

interface AccountTable extends Table<Account> {
  // Add any table-specific methods here
}

interface PMMaxDatabaseTables {
  accounts: AccountTable;
  campaignData: Table<DataStore>;
  onboardingData: Table<OnboardingStorageData>;
  bfData: Table<BFStorageData>;
}
export class PMMaxDatabase extends Dexie {
  campaignData!: Table<DataStore>;
  accounts!: AccountTable;
  onboardingData!: Table<OnboardingStorageData>;
  bfData!: Table<BFStorageData>;
  private initialized = false;
  private initPromise: Promise<void>;
  metadata!: Table<{ key: string; value: string }>;

  private debouncedUpdate = debounce(
    async (account: Account) => {
      try {
        await this.accounts.put(account);
        log("Background Update", {
          dexieId: account.dexieId,
          success: true,
        });
      } catch (err) {
        console.error("Background update failed:", err);
        throw err;
      }
    },
    1000, // 1 second delay
  );

  constructor() {
    console.log("PMMaxDatabase: Constructor starting");
    super("pmaxData");

    // Simple schema - no forced sample data
    this.version(1).stores({
      campaignData: "++id,lastUpdated,dexieId",
      accounts: "++id,accountCID,dexieId",
      metadata: "key",
    });

    // Version 2: Add onboarding data table
    this.version(2).stores({
      campaignData: "++id,lastUpdated,dexieId",
      accounts: "++id,accountCID,dexieId",
      metadata: "key",
      onboardingData: "++id,dexieId,timestamp",
    });

    // Version 3: Add Black Friday data table
    this.version(3).stores({
      campaignData: "++id,lastUpdated,dexieId",
      accounts: "++id,accountCID,dexieId",
      metadata: "key",
      onboardingData: "++id,dexieId,timestamp",
      bfData: "++id,dexieId,timestamp",
    });

    // Initialize database and sample data
    this.initPromise = this.initialize();
  }

  private async initialize() {
    try {
      console.log("DB initialize starting");
      await this.open();
      console.log("DB opened successfully");

      const hasAccounts = await this.isInitialized();
      console.log("DB initialization check:", { hasAccounts });

      if (!hasAccounts) {
        console.log("Loading sample account...");
        await this.loadSampleAccount();
        console.log("Sample account loaded");
      }

      this.initialized = true;
      console.log("DB initialization complete");
    } catch (err) {
      console.error("DB initialization failed:", err);
      throw err;
    }
  }

  public async ready() {
    await this.initPromise;
    return this.initialized;
  }

  async getAllAccounts(): Promise<Account[]> {
    await this.ready();
    return this.accounts.toArray();
  }

  async getAccount(dexieId: string): Promise<Account | undefined> {
    //console.log('getAccount called:', { dexieId })
    if (!dexieId) {
      console.log("No dexieId provided");
      return undefined;
    }
    const account = await this.accounts.get(dexieId);
    console.log("getAccount result:", {
      found: !!account,
      account: account
        ? {
            dexieId: account.dexieId,
            name: account.name,
          }
        : null,
    });
    return account;
  }

  async isInitialized(): Promise<boolean> {
    try {
      const accountCount = await this.accounts.count();
      const sampleAccount = await this.getAccount(SAMPLE_ACCOUNT_ID);
      return accountCount > 0 && !!sampleAccount;
    } catch (err) {
      console.error("Database check failed:", err);
      return false;
    }
  }

  async getAccountData(dexieId: string): Promise<StorageData | undefined> {
    try {
      // Query by dexieId field since it's not the primary key
      const data = await this.campaignData
        .where("dexieId")
        .equals(dexieId)
        .first();

      if (data) {
        const dataSize = JSON.stringify(data).length / 1024;
        const tabCounts = Object.entries(SHEET_TABS).map(([key, tabName]) => ({
          tab: tabName,
          count: Array.isArray(data[tabName]) ? data[tabName].length : "N/A",
        }));

        console.log(`[DB] 📖 RETRIEVED DATA for ${dexieId}:`, {
          version: data.version,
          timestamp: data.timestamp,
          dataSize: dataSize.toFixed(1) + "KB",
          tabsWithData: tabCounts.filter(
            (t) => typeof t.count === "number" && t.count > 0,
          ).length,
          sampleCounts: tabCounts.slice(0, 5), // Show first 5 for debugging
        });
      } else {
        console.log(`[DB] ❌ NO DATA found for ${dexieId} in IndexedDB`);
      }

      return data;
    } catch (err) {
      console.error(`[DB] 💥 FAILED to retrieve data for ${dexieId}:`, err);
      return undefined;
    }
  }

  async saveAccountData(dexieId: string, data: StorageData): Promise<void> {
    try {
      const dataToStore = {
        ...data,
        lastUpdated: new Date().toISOString(),
        dexieId,
      };

      // Enhanced logging to debug what we're actually saving
      const dataSize = JSON.stringify(dataToStore).length / 1024;
      const tabCounts = Object.entries(SHEET_TABS).map(([key, tabName]) => ({
        tab: tabName,
        count: Array.isArray(dataToStore[tabName])
          ? dataToStore[tabName].length
          : "N/A",
      }));

      console.log(`[DB] 💾 SAVING DATA for ${dexieId}:`, {
        version: dataToStore.version,
        timestamp: dataToStore.timestamp,
        dataSize: dataSize.toFixed(1) + "KB",
        totalTabs: Object.keys(SHEET_TABS).length,
        tabsWithData: tabCounts.filter(
          (t) => typeof t.count === "number" && t.count > 0,
        ).length,
        sampleCounts: tabCounts
          .slice(0, 5)
          .concat(tabCounts.filter((t) => t.tab.startsWith("on_"))), // Show first 5 + any tab that starts with 'on_'
      });

      // Delete existing data first
      const deletedCount = await this.campaignData
        .where("dexieId")
        .equals(dexieId)
        .delete();

      if (deletedCount > 0) {
        console.log(
          `[DB] 🗑️ Deleted ${deletedCount} existing records for ${dexieId}`,
        );
      }

      // Add new data
      const id = await this.campaignData.add(dataToStore);

      console.log(
        `[DB] ✅ SAVED DATA for ${dexieId} with ID ${id}, size: ${dataSize.toFixed(1)}KB`,
      );

      log("Write", {
        operation: "save",
        dexieId,
        timestamp: dataToStore.lastUpdated,
        dataSize: dataSize.toFixed(1) + "KB",
        tabsWithData: tabCounts.filter(
          (t) => typeof t.count === "number" && t.count > 0,
        ).length,
      });
    } catch (err) {
      console.error(`[DB] 💥 FAILED to save data for ${dexieId}:`, err);
      throw err;
    }
  }

  async getSampleData(): Promise<DataStore | undefined> {
    try {
      const data = await this.campaignData
        .where("dexieId")
        .equals(SAMPLE_ACCOUNT_ID)
        .first(); // Changed from .last() to .first() for consistency

      log("Read Sample Data", {
        exists: !!data,
        timestamp: data?.lastUpdated,
        dataSize: data ? JSON.stringify(data).length / 1024 + "KB" : 0,
      });

      return data;
    } catch (err) {
      console.error("Failed to get sample data:", err);
      return undefined;
    }
  }

  // Black Friday data methods
  async getBFData(accountCID: string): Promise<BFStorageData | undefined> {
    try {
      const data = await this.bfData
        .where("dexieId")
        .equals(accountCID)
        .first();

      if (data) {
        const dataSize = JSON.stringify(data).length / 1024;
        console.log(`[DB BF] 📖 Retrieved BF data for ${accountCID}:`, {
          timestamp: data.timestamp,
          dataSize: dataSize.toFixed(1) + "KB",
          hasDashboard: !!data.dashboard,
          hasProducts25: !!data.products25?.length,
          hasSearchTerms25: !!data.searchTerms25?.length,
        });
      } else {
        console.log(`[DB BF] ❌ No BF data found for ${accountCID}`);
      }

      return data;
    } catch (err) {
      console.error(`[DB BF] 💥 Failed to retrieve BF data for ${accountCID}:`, err);
      return undefined;
    }
  }

  async saveBFData(accountCID: string, data: BFStorageData): Promise<void> {
    try {
      const dataToStore = {
        ...data,
        timestamp: new Date().toISOString(),
        dexieId: accountCID,
      };

      const dataSize = JSON.stringify(dataToStore).length / 1024;
      console.log(`[DB BF] 💾 Saving BF data for ${accountCID}:`, {
        timestamp: dataToStore.timestamp,
        dataSize: dataSize.toFixed(1) + "KB",
        hasDashboard: !!dataToStore.dashboard,
        products25Count: dataToStore.products25?.length || 0,
        searchTerms25Count: dataToStore.searchTerms25?.length || 0,
        dailyCampaign25Count: dataToStore.dailyCampaign25?.length || 0,
      });

      // Delete existing data first
      const deletedCount = await this.bfData
        .where("dexieId")
        .equals(accountCID)
        .delete();

      if (deletedCount > 0) {
        console.log(`[DB BF] 🗑️ Deleted ${deletedCount} existing BF records for ${accountCID}`);
      }

      // Add new data
      const id = await this.bfData.add(dataToStore);

      console.log(`[DB BF] ✅ Saved BF data for ${accountCID} with ID ${id}, size: ${dataSize.toFixed(1)}KB`);
    } catch (err) {
      console.error(`[DB BF] 💥 Failed to save BF data for ${accountCID}:`, err);
      throw err;
    }
  }

  async clearBFData(accountCID: string): Promise<void> {
    try {
      const deletedCount = await this.bfData
        .where("dexieId")
        .equals(accountCID)
        .delete();

      console.log(`[DB BF] 🗑️ Cleared ${deletedCount} BF records for ${accountCID}`);
    } catch (err) {
      console.error(`[DB BF] 💥 Failed to clear BF data for ${accountCID}:`, err);
      throw err;
    }
  }

  // Debug function to list all campaign data
  async debugListAllCampaignData(): Promise<void> {
    try {
      const allData = await this.campaignData.toArray();
      console.log(
        `[DB] 🔍 DEBUG: Found ${allData.length} campaign data records in IndexedDB:`,
      );

      allData.forEach((record, index) => {
        const dataSize = JSON.stringify(record).length / 1024;
        const tabCounts = Object.entries(SHEET_TABS).map(([key, tabName]) => ({
          tab: tabName,
          count: Array.isArray(record[tabName])
            ? record[tabName].length
            : "N/A",
        }));

        console.log(`[DB] 📋 Record ${index + 1}:`, {
          dexieId: record.dexieId,
          version: record.version,
          timestamp: record.timestamp,
          dataSize: dataSize.toFixed(1) + "KB",
          tabsWithData: tabCounts.filter(
            (t) => typeof t.count === "number" && t.count > 0,
          ).length,
        });
      });
    } catch (err) {
      console.error("[DB] Failed to list campaign data:", err);
    }
  }

  async saveData(
    data: StorageData,
    isSampleData: boolean = false,
  ): Promise<void> {
    if (isSampleData) {
      await this.clearSampleData();
    } else {
      await this.clearUserData();
    }

    const dataToStore: DataStore = {
      ...data,
      lastUpdated: new Date().toISOString(),
      dexieId: isSampleData ? SAMPLE_ACCOUNT_ID : data.dexieId,
    };

    log("Write", {
      operation: "save",
      isSample: isSampleData,
      timestamp: dataToStore.lastUpdated,
      dataSize: JSON.stringify(dataToStore).length / 1024 + "KB",
    });

    await this.campaignData.add(dataToStore);

    const savedData = await this.campaignData
      .where("lastUpdated")
      .equals(dataToStore.lastUpdated)
      .first();

    if (!savedData) {
      throw new Error("Failed to save data to database");
    }
  }

  async clearUserData(): Promise<void> {
    log("Clear", "Clearing user data");
    const count = await this.campaignData
      .where("dexieId")
      .equals(SAMPLE_ACCOUNT_ID)
      .delete();

    log("Clear Complete", {
      type: "user",
      recordsDeleted: count,
    });
  }

  async clearSampleData(): Promise<void> {
    log("Clear", "Clearing sample data");
    const count = await this.campaignData
      .where("dexieId")
      .equals(SAMPLE_ACCOUNT_ID)
      .delete();

    log("Clear Complete", {
      type: "sample",
      recordsDeleted: count,
    });
  }

  async getStats(): Promise<{
    accounts: number;
    totalRecords: number;
    totalSize: number;
  }> {
    const [accounts, allData] = await Promise.all([
      this.accounts.count(),
      this.campaignData.toArray(),
    ]);

    const totalSize = JSON.stringify(allData).length / 1024;

    log("Stats", {
      accounts,
      records: allData.length,
      totalSize: `${totalSize.toFixed(2)}KB`,
    });

    return {
      accounts,
      totalRecords: allData.length,
      totalSize,
    };
  }

  async saveAccount(account: Account): Promise<void> {
    if (!account.dexieId) {
      throw new Error("Account missing dexieId");
    }
    await this.accounts.put(account);
  }

  async deleteAccount(dexieId: string): Promise<void> {
    const account = await this.getAccount(dexieId);
    if (!account) throw new Error("Account not found");

    if (account.isSampleAccount && process.env.NODE_ENV !== "development") {
      throw new Error("Cannot delete sample account");
    }

    await this.accounts.delete(dexieId);
    await this.campaignData.where("dexieId").equals(dexieId).delete();
  }

  async addAccount(account: Account): Promise<Account> {
    // Create the account with all required fields
    const accountToAdd: Account = {
      ...account,
      id: account.accountCID,
      dexieId: account.accountCID,
      accountCID: account.accountCID,
    };

    // Add account first
    await this.accounts.put(accountToAdd);

    // Don't initialize empty data - let the campaign context handle data fetching
    return accountToAdd;
  }

  async updateAccount(account: Account, background = false): Promise<void> {
    if (!account.dexieId) {
      throw new Error("dexieId is required for account updates");
    }

    try {
      // Skip reading existing account - we don't need it for simple field updates
      // Just preserve system fields that shouldn't change
      const updatedAccount = {
        ...account,
        id: account.dexieId,
        dexieId: account.dexieId,
        accountCID: account.accountCID || account.dexieId,
        isSampleAccount: account.isSampleAccount ?? false,
        webAppUrl: account.webAppUrl,
      };

      if (DEBUG) {
        console.log("DB Update:", {
          dexieId: updatedAccount.dexieId,
          updates: account,
          result: updatedAccount,
          changedFields: Object.keys(account),
        });
      }

      if (background) {
        this.debouncedUpdate(updatedAccount);
        return;
      }

      await this.accounts.put(updatedAccount);
    } catch (err) {
      console.error("DB update failed:", err);
      throw err;
    }
  }

  async inspectDB() {
    console.group("🔍 Dexie DB Inspection");
    try {
      const accounts = await this.accounts.toArray();
      console.log("Raw accounts table:", accounts);

      const campaignData = await this.campaignData.toArray();
      console.log("Raw campaign data:", campaignData);

      // Show any accounts with sample-account as dexieId
      const brokenAccounts = accounts.filter(
        (a) => a.dexieId === "sample-account",
      );
      if (brokenAccounts.length) {
        console.warn(
          "⚠️ Found accounts with hardcoded sample-account ID:",
          brokenAccounts,
        );
      }

      // Show actual Dexie-generated IDs
      console.log(
        "All dexieIds:",
        accounts.map((a) => ({
          dexieId: a.dexieId,
          name: a.name,
          accountCID: a.accountCID,
        })),
      );
    } catch (err) {
      console.error("Failed to inspect DB:", err);
    }
    console.groupEnd();
  }

  async loadSampleAccount(): Promise<Account> {
    const existing = await this.getAccount(SAMPLE_ACCOUNT_ID);
    if (existing) return existing;

    const sampleAccount: Account = {
      id: SAMPLE_ACCOUNT_ID,
      dexieId: SAMPLE_ACCOUNT_ID,
      accountCID: SAMPLE_ACCOUNT_ID,
      name: "Sample Account",
      isSampleAccount: true,
      businessMode: "ecomm",
      currency: "$",
      cogsAmount: 55,
      breakEvenCpa: 33,
      webAppUrl: "/sample-data",
      scriptVersion: "0.8.0",
    };

    await this.accounts.add(sampleAccount);

    // Sample data will be loaded on-demand when user switches to sample account
    console.log("Sample account created - data will load on demand");

    return sampleAccount;
  }

  async ensureSampleAccount(): Promise<Account> {
    try {
      const existing = await this.getAccount(SAMPLE_ACCOUNT_ID);
      if (existing) return existing;

      // If no sample account exists, create one
      return await this.loadSampleAccount();
    } catch (err) {
      console.error("Failed to ensure sample account:", err);
      // Create a new sample account as fallback
      const sampleAccount: Account = {
        id: SAMPLE_ACCOUNT_ID,
        dexieId: SAMPLE_ACCOUNT_ID,
        accountCID: SAMPLE_ACCOUNT_ID,
        name: "Sample Account",
        isSampleAccount: true,
        businessMode: "ecomm",
        currency: "$",
        cogsAmount: 55,
        breakEvenCpa: 33,
        webAppUrl: "/sample-data",
        scriptVersion: "0.8.0",
      };

      // Add to DB
      await this.accounts.add(sampleAccount);
      return sampleAccount;
    }
  }

  async getLastActiveAccount(): Promise<string | null> {
    const record = await this.metadata.get("lastActiveAccount");
    return record?.value || null;
  }

  async setLastActiveAccount(dexieId: string): Promise<void> {
    await this.metadata.put({
      key: "lastActiveAccount",
      value: dexieId,
    });
  }

  async updateAccountRefreshTime(dexieId: string): Promise<void> {
    const account = await this.accounts.get(dexieId);
    if (account) {
      await this.accounts.put({
        ...account,
        lastRefreshed: new Date().toISOString(),
      });
    }
  }

  async deleteAccountData(dexieId: string): Promise<void> {
    await this.campaignData.delete(dexieId);
  }

  async getAccounts(): Promise<Account[]> {
    return this.accounts.toArray();
  }
}

export const db = new PMMaxDatabase();

// Export ready promise for easier initialization checking
export const dbReady = db.ready();
